import React, { useState, useEffect, useCallback, useRef } from 'react';
import Filters from '../Filters';
import API from '../../services';
import './style.scss';

const LocateForm = ({
    locationInputRef,
    updateMembers,
    setHasFilters,
    hasFilters,
    setHasSearch,
    hasSearch,
    setPendingRequests,
    pagination,
    googleApis
}) => {
    const [mapInitialized, setMapInitialized] = useState(false);
    const [location, setLocation] = useState('');
    const [memberName, setMemberName] = useState('');
    const [proximity, setProximity] = useState(75);
    const [filterSelections, setFilterSelections] = useState({ proximity: [75] });

    const handleFilterChange = useCallback((e) => {
        const value = parseInt(e.target.value);
        if (e.target.name === 'proximity') {
            setProximity(value);
        }
        if (e.target.type === 'radio') {
            setFilterSelections({
                ...filterSelections,
                [e.target.name]: [value]
            });
        } else if (e.target.type === 'checkbox') {
            const facet = new Set(filterSelections[e.target.name] || []);

            if (e.target.checked) {
                facet.add(value);
            } else {
                facet.delete(value);
            }
            setFilterSelections({
                ...filterSelections,
                [e.target.name]: [...facet]
            });
        }
    }, [filterSelections]);

    const memberNameInputRef = useRef(null);

    const handleClearAll = useCallback(() => {
        setFilterSelections(state => ({ 'proximity': state.proximity ?? [100] }));
        setMemberName('');
        setLocation(null);
        locationInputRef.current.value = '';
        memberNameInputRef.current.value = '';
    }, []);

    const firstRender = useRef(true);

    useEffect(() => {
        console.log('searching with', {
            location,
            memberName,
            filterSelections,
            pagination: pagination.page,
        });
        const emptySearch = !location && !memberName;
        setHasSearch(!emptySearch);

        setHasFilters(0 < Object.entries(filterSelections).reduce((r, [name, selections]) => (
            r + (name === 'proximity' ? 0 : selections.length)
        ), 0));

        if (emptySearch) {
            if (firstRender.current) {
                firstRender.current = false;
            } else {
                updateMembers({ lastPage: 0, page: 1, pageSize: 50, totalCount: 0, items: []});
            }
            return;
        }
        setPendingRequests(current => current + 1);
        API.searchProfiles(
            location ? location.lat : null,
            location ? location.lng : null,
            location ? proximity : null,
            filterSelections['focusAreas'],
            filterSelections['populationsServed'],
            filterSelections['languagesSpoken'],
            filterSelections['serviceModalities'],
            memberName,
            pagination.page
        ).then((response) => {
            updateMembers(response);
            setPendingRequests(current => current - 1);
        }).catch((e) => {
            console.error('an error occured during search', e);
            updateMembers({ lastPage: 0, page: 1, pageSize: 50, totalCount: 0, items: []});
            setPendingRequests(current => current - 1);
        });
    }, [filterSelections, setPendingRequests, pagination.page, location, memberName]);

    useEffect(() => {
        if (googleApis.map && !mapInitialized) {
            setMapInitialized(true);
            window.google.maps.event.addListener(
                googleApis.autocomplete,
                'place_changed',
                () => {
                    let place = googleApis.autocomplete.getPlace();
                    if (place && place.geometry && place.geometry.location) {
                        setLocation({
                            lat: place.geometry.location.lat(),
                            lng: place.geometry.location.lng(),
                        });
                        locationInputRef.current.value = place.formatted_address;
                        setMemberName(memberNameInputRef.current.value ?? '');
                    } else {
                        setLocation(null);
                        locationInputRef.current.value = '';
                    }

                }
            );
        }
    }, [googleApis, mapInitialized, locationInputRef.current, memberNameInputRef.current]);


    return (
        <form className="cbdce-education-finder__locate-form">
            <h4 className="cbdce-heading cbdce-heading--lg cbdce-heading--bm-40">Search</h4>
            <div className="form-group">
                <label className="control-label" htmlFor="location">Search by Location</label>
                <input
                    type="text"
                    name="location"
                    ref={locationInputRef}
                    placeholder=""
                    className="form-control"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            if (!e.target.value) {
                                setLocation(null);
                                locationInputRef.current.value = '';
                            }
                        }
                    }}
                    onBlur={(e) => {
                        if (!e.target.value) {
                            setLocation(null);
                        }
                    }}
                />
                <div className="form-feedback-group">
                    <span className="form-help-text">
                        Enter City, State, or Zip
                    </span>
                </div>
            </div>

            <div className="form-group">
                <label className="control-label" htmlFor="member-name">Search by Last Name</label>
                <input
                    type="text"
                    name="member-name"
                    ref={memberNameInputRef}
                    placeholder=""
                    className="form-control"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setMemberName(memberNameInputRef.current?.value ?? '');
                        }
                    }}
                />
            </div>

            <button
                type="button"
                onClick={() => setMemberName(memberNameInputRef.current?.value ?? '')}
                className="cbdce-button btn btn-dark btn-block"
            >
                <span className="cbdce-button__label">
                    Search Directory
                </span>
                <i className="fa-solid fa-magnifying-glass" />
            </button>

            <Filters
                filterSelections={filterSelections}
                hasLocation={!!location}
                hasFilters={hasFilters}
                hasSearch={hasSearch}
                handleClearAll={handleClearAll}
                handleFilterChange={handleFilterChange}
            />
        </form>
    );
};

export default LocateForm;
